import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { LOGOSHealthKit, WatchBiometrics, ECGData } from '../plugins/logos-healthkit';

export interface HealthKitState {
  available: boolean;
  authorized: boolean;
  biometrics: WatchBiometrics | null;
  ecg: ECGData | null;
  loading: boolean;
  error: string | null;
  lastSync: string | null;
}

export interface DimensionAutoPopulation {
  sleep: number | null;
  exercise: number | null;
  energy: number | null;
  peace: number | null;
  meditation: number | null;
  gratitude: number | null;
  joy: number | null;
}

const STORAGE_KEY = 'logos-healthkit-biometrics';
const SYNC_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

function clamp(v: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Map Watch biometrics to LOGOS dimension values (0-1 scale).
 * Returns null for dimensions that can't be computed from available data.
 */
export function mapBiometricsToDimensions(bio: WatchBiometrics): DimensionAutoPopulation {
  const result: DimensionAutoPopulation = {
    sleep: null,
    exercise: null,
    energy: null,
    peace: null,
    meditation: null,
    gratitude: null,
    joy: null,
  };

  // SLEEP: composite formula using duration + stages
  if (bio.sleepHours >= 0) {
    const durationScore = clamp(bio.sleepHours / 8.0); // 8h = 1.0
    let stageBonus = 0;
    if (bio.sleepStages) {
      const { deep, rem } = bio.sleepStages;
      // Deep sleep: ideal 1.5-2h, REM: ideal 1.5-2h
      if (deep >= 0) stageBonus += clamp(deep / 1.5) * 0.15;
      if (rem >= 0) stageBonus += clamp(rem / 1.5) * 0.15;
    }
    result.sleep = clamp(durationScore * 0.7 + stageBonus + 0.0);
  }

  // EXERCISE: minutes-based (WHO recommends 30min/day)
  if (bio.exerciseMinutes >= 0) {
    result.exercise = clamp(bio.exerciseMinutes / 30.0);
  }

  // ENERGY: multi-factor (RHR + VO2max + activeEnergy)
  {
    let factors = 0;
    let sum = 0;
    if (bio.restingHeartRate > 0) {
      // Lower RHR = better. 40bpm=1.0, 80bpm=0.0
      sum += clamp(1.0 - (bio.restingHeartRate - 40) / 40);
      factors++;
    }
    if (bio.vo2Max > 0) {
      // VO2max: 20=0.0, 50+=1.0
      sum += clamp((bio.vo2Max - 20) / 30);
      factors++;
    }
    if (bio.activeEnergy > 0) {
      // Active energy: 0-500 kcal = 0-1
      sum += clamp(bio.activeEnergy / 500);
      factors++;
    }
    if (factors > 0) {
      result.energy = clamp(sum / factors);
    }
  }

  // PEACE: dual-factor (HRV + respiratory rate)
  {
    let factors = 0;
    let sum = 0;
    if (bio.hrv > 0) {
      // HRV SDNN: 20ms=0.0, 100ms=1.0
      sum += clamp((bio.hrv - 20) / 80);
      factors++;
    }
    if (bio.respiratoryRate > 0) {
      // Respiratory: 12-20 normal. 12=1.0, 20=0.5, 30=0.0
      sum += clamp(1.0 - (bio.respiratoryRate - 12) / 18);
      factors++;
    }
    if (factors > 0) {
      result.peace = clamp(sum / factors);
    }
  }

  // MEDITATION: mindful minutes
  if (bio.mindfulMinutes >= 0) {
    // 0min=0.0, 20min=1.0
    result.meditation = clamp(bio.mindfulMinutes / 20.0);
  }

  return result;
}

export function useHealthKit() {
  const [state, setState] = useState<HealthKitState>({
    available: false,
    authorized: false,
    biometrics: null,
    ecg: null,
    loading: false,
    error: null,
    lastSync: null,
  });

  const isNative = Capacitor.isNativePlatform();

  // Check availability on mount
  useEffect(() => {
    if (!isNative) return;

    LOGOSHealthKit.isAvailable().then(({ available }) => {
      setState(s => ({ ...s, available }));
    }).catch(() => {
      setState(s => ({ ...s, available: false }));
    });

    // Load cached biometrics
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        setState(s => ({
          ...s,
          biometrics: parsed.biometrics,
          ecg: parsed.ecg,
          lastSync: parsed.lastSync,
        }));
      }
    } catch { /* ignore */ }
  }, [isNative]);

  // Request HealthKit authorization
  const requestAuth = useCallback(async () => {
    if (!isNative) return false;
    try {
      const { authorized } = await LOGOSHealthKit.requestAuthorization();
      setState(s => ({ ...s, authorized, error: null }));
      return authorized;
    } catch (err: any) {
      setState(s => ({ ...s, error: err.message }));
      return false;
    }
  }, [isNative]);

  // Fetch all biometrics from HealthKit
  const syncBiometrics = useCallback(async (daysBack = 1) => {
    if (!isNative) return null;

    setState(s => ({ ...s, loading: true, error: null }));

    try {
      const [biometrics, ecg] = await Promise.all([
        LOGOSHealthKit.readAllBiometrics({ daysBack }),
        LOGOSHealthKit.readECGResults({ daysBack: 7 }),
      ]);

      const lastSync = new Date().toISOString();

      // Cache locally
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ biometrics, ecg, lastSync }));
      } catch { /* ignore */ }

      setState(s => ({
        ...s,
        biometrics,
        ecg,
        loading: false,
        lastSync,
        authorized: true,
      }));

      return biometrics;
    } catch (err: any) {
      setState(s => ({ ...s, loading: false, error: err.message }));
      return null;
    }
  }, [isNative]);

  // Auto-sync on interval
  useEffect(() => {
    if (!isNative || !state.authorized) return;

    const interval = setInterval(() => {
      syncBiometrics(1);
    }, SYNC_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isNative, state.authorized, syncBiometrics]);

  // Get auto-populated dimension values
  const autoPopulatedDimensions = state.biometrics
    ? mapBiometricsToDimensions(state.biometrics)
    : null;

  return {
    ...state,
    isNative,
    requestAuth,
    syncBiometrics,
    autoPopulatedDimensions,
  };
}
